// Dependency-free report exporters.
//
//  - PDF / Print : a dedicated print window containing ONLY the rendered report
//                  (vector charts → crisp, branded PDF). Never the editor UI.
//  - CSV / XLS   : the aggregated tabular data behind the report's widgets.
//  - HTML        : a self-contained file (preview DOM + inlined stylesheets).
//  - PNG         : best-effort rasterisation of the preview via SVG foreignObject.

import type { CacheEntry } from "./reportApi";

function download(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slug(s: string): string {
  return (s || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "report";
}

// Print / PDF export.
//
// We print the report *within this same document* rather than opening a new
// window. The previous new-window approach (inline the collected CSS into a
// blob-URL pop-up, then drive window.print() from the parent) kept printing
// blank pages on the live site: the re-collected Tailwind stylesheet didn't
// reliably apply in the fresh document, so the branded header/footer showed but
// every widget/chart came out empty.
//
// Staying in the live document sidesteps that entirely. The ReportPreview is
// always mounted (off-screen in edit mode), so its SVG charts and tables are
// already rendered and already styled by the app's loaded stylesheet. We clone
// that live node into a print-only container and use @media print to show ONLY
// the clone — no CSS to re-collect, no pop-up, no CSP/blob timing traps.
const PRINT_ROOT_ID = "__guardai_print_root";
const PRINT_STYLE_ID = "__guardai_print_style";

export async function printReport(node: HTMLElement | null) {
  if (!node) {
    // Nothing to print from — fall back to the plain page print.
    window.print();
    return;
  }

  // Clear any leftovers from an earlier (possibly interrupted) print run.
  document.getElementById(PRINT_ROOT_ID)?.remove();
  document.getElementById(PRINT_STYLE_ID)?.remove();

  // Give any still-loading widgets (data fetch / images) a bounded chance to
  // finish so the snapshot shows charts and tables rather than spinners.
  await waitForContent(node, 4000);

  // Clone the live, fully-rendered report so we never disturb React's DOM.
  const clone = node.cloneNode(true) as HTMLElement;

  const printRoot = document.createElement("div");
  printRoot.id = PRINT_ROOT_ID;
  printRoot.appendChild(clone);
  document.body.appendChild(printRoot);

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = printStyles(PRINT_ROOT_ID);
  document.head.appendChild(style);

  const cleanup = () => {
    document.getElementById(PRINT_ROOT_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);

  // Let fonts settle and layout flush, then print. afterprint cleans up; a long
  // fallback timer covers browsers that never fire it (or a cancelled dialog).
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* fonts API rejected — print anyway */
    }
  }
  await new Promise((r) => setTimeout(r, 120));
  window.print();
  setTimeout(cleanup, 60_000);
}

// Resolve once the report node has no loading spinners left and every image has
// finished loading — or once the timeout elapses, so we never hang.
function waitForContent(node: HTMLElement, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const spinning = node.querySelector(".animate-spin");
      const imgs = Array.prototype.slice.call(node.querySelectorAll("img")) as HTMLImageElement[];
      const imagesPending = imgs.some((img) => !!img.getAttribute("src") && !img.complete);
      if ((!spinning && !imagesPending) || Date.now() - started > timeoutMs) {
        resolve();
        return;
      }
      setTimeout(tick, 120);
    };
    tick();
  });
}

// Print stylesheet: hide the live app, show only the cloned report, and keep the
// branded colours + compact header. All rules are scoped to the print container.
function printStyles(rootId: string): string {
  return `
    /* Invisible on screen — the clone exists purely for the print snapshot. */
    #${rootId} { display: none; }

    @media print {
      /* Show ONLY the cloned report; hide the live app, modals, toasts, etc. */
      body > *:not(#${rootId}) { display: none !important; }
      #${rootId} { display: block !important; }

      /* The global stylesheet's print rules isolate the report with
         \`body * { visibility: hidden }\` (to support a plain browser Cmd+P of the
         live off-screen preview). That universal rule ALSO matches this cloned
         print root — a body descendant — so without re-asserting visibility the
         clone renders \`display:block\` but fully invisible, which is exactly why
         exported/printed reports came out blank (only the browser's own header/
         footer showed). Re-assert visibility on the clone so it always paints.
         \`#id *\` (specificity 1,0,0) beats the global \`body *\` (0,0,2). */
      #${rootId}, #${rootId} * { visibility: visible !important; }

      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #ffffff !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      #${rootId}, #${rootId} * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .no-print { display: none !important; }

      #${rootId} .report-preview {
        margin: 0 auto !important;
        max-width: none !important;
        box-shadow: none !important;
      }

      /* Keep each widget card whole across page boundaries. */
      #${rootId} .break-inside-avoid,
      #${rootId} [class*="break-inside-avoid"] {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      @page { margin: 12mm; }

      /* Compact the branded header so the first widget shares page 1 with it
         instead of being pushed to a blank page 2. */
      #${rootId} .report-preview > div:first-child {
        padding-top: 14px !important;
        padding-bottom: 14px !important;
        border-radius: 0 !important;
        break-after: avoid;
        page-break-after: avoid;
      }
      /* Tighten the body wrapper and the gaps between widgets. */
      #${rootId} .report-preview > div:last-child {
        padding-top: 12px !important;
        padding-bottom: 12px !important;
        background: #ffffff !important;
        border-radius: 0 !important;
      }
      #${rootId} .report-preview .grid { gap: 12px !important; }
      #${rootId} .report-preview .mt-8 { margin-top: 16px !important; }

      /* Let the first widget start on page 1 next to the header rather than
         jumping to its own page (which left page 1 mostly blank). */
      #${rootId} .report-preview .grid > :first-child {
        break-inside: auto !important;
        page-break-inside: auto !important;
      }
    }
  `;
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------
function csvCell(v: string | number): string {
  // Neutralise spreadsheet formulas (labels can come from QR payload text).
  let s = String(v);
  if (typeof v !== "number" && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportCSV(title: string, entries: CacheEntry[]) {
  const lines: string[] = [];
  const data = entries.filter((e) => e.rows.length > 0);
  if (!data.length) {
    lines.push("No tabular data in this report.");
  }
  for (const e of data) {
    lines.push(csvCell(e.title));
    lines.push([csvCell(e.groupBy || "Group"), csvCell(e.metric)].join(","));
    for (const r of e.rows) lines.push([csvCell(r.label), csvCell(r.value)].join(","));
    lines.push(["Total", csvCell(e.total)].join(","));
    lines.push("");
  }
  download(`${slug(title)}.csv`, new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
}

// ---------------------------------------------------------------------------
// XLS (lightweight: an HTML table that Excel/Sheets opens natively as .xls)
// ---------------------------------------------------------------------------
export function exportXLS(title: string, entries: CacheEntry[]) {
  const esc = (s: string | number) =>
    String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const tables = entries
    .filter((e) => e.rows.length > 0)
    .map((e) => {
      const body = e.rows.map((r) => `<tr><td>${esc(r.label)}</td><td>${esc(r.value)}</td></tr>`).join("");
      return `<tr><th colspan="2" style="background:#1e3a8a;color:#fff;text-align:left">${esc(e.title)}</th></tr>
        <tr><th>${esc(e.groupBy || "Group")}</th><th>${esc(e.metric)}</th></tr>
        ${body}
        <tr><td><b>Total</b></td><td><b>${esc(e.total)}</b></td></tr>
        <tr><td></td><td></td></tr>`;
    })
    .join("");
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
    <head><meta charset="utf-8"></head><body><table border="1">${tables}</table></body></html>`;
  download(`${slug(title)}.xls`, new Blob([html], { type: "application/vnd.ms-excel" }));
}

// ---------------------------------------------------------------------------
// Standalone HTML (faithful — inlines all page stylesheets)
// ---------------------------------------------------------------------------
async function collectCSS(): Promise<string> {
  let css = "";
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      css += Array.from((sheet as CSSStyleSheet).cssRules)
        .map((r) => r.cssText)
        .join("\n");
    } catch {
      const href = (sheet as CSSStyleSheet).href;
      if (href) {
        try {
          css += await (await fetch(href)).text();
        } catch {
          /* cross-origin sheet we can't read — skip */
        }
      }
    }
  }
  return css;
}

export async function exportHTML(title: string, node: HTMLElement) {
  const css = await collectCSS();
  // Inline <img> sources as data URLs. The exported file opens from file://,
  // where site-absolute paths like /g-data-logo-no-bg.png can't resolve — the
  // report header logo showed as a broken image until it was embedded.
  const clone = node.cloneNode(true) as HTMLElement;
  await inlineImages(node, clone);
  const html = `<!doctype html><html><head><meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${String(title).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>)[c])}</title>
    <style>${css}\nbody{background:#f4f6fb;margin:0;padding:24px;font-family:Inter,system-ui,sans-serif;}</style>
    </head><body>${clone.outerHTML}</body></html>`;
  download(`${slug(title)}.html`, new Blob([html], { type: "text/html;charset=utf-8" }));
}

// ---------------------------------------------------------------------------
// PNG (best-effort) — inline images as data URLs, then rasterise via SVG.
// ---------------------------------------------------------------------------
// The PNG path embeds the page CSS inside an SVG, which the browser parses as
// XML. Raw CSS routinely contains characters that are illegal in XML text — a
// bare `&` (e.g. `@media screen and (...)` is fine, but `content: "&"`, URL
// query strings, and Tailwind's nested `&` selectors are not), or a stray `<`.
// Any one of these makes the SVG unparseable, so the <img> load fails and we
// surface "PNG rendering failed". Wrapping the CSS in a CDATA section (kept
// valid CSS by fencing the markers inside comments) lets both parsers cope.
function cssForSvg(css: string): string {
  const safe = css.replace(/]]>/g, "]] >");
  return `/*<![CDATA[*/\n${safe}\n/*]]>*/`;
}

async function imgToDataURL(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Rewrite every <img> in `clone` to a self-contained data: URL, reading the
 * *resolved* .src off the live originals (absolute URL). Used by both the
 * HTML export (file:// can't resolve site-absolute paths like the header
 * logo) and the PNG export (external image URLs would taint the canvas).
 * `srcset`/`crossorigin` are stripped as well: a surviving `srcset` lets the
 * browser pick a non-inlined candidate and ignore the data: `src` entirely.
 * Call BEFORE any transform that inserts extra <img> nodes into the clone —
 * the two lists are paired by index.
 */
async function inlineImages(node: HTMLElement, clone: HTMLElement): Promise<void> {
  const originals = Array.from(node.querySelectorAll("img"));
  const clones = Array.from(clone.querySelectorAll("img"));
  await Promise.all(
    clones.map(async (img, i) => {
      img.removeAttribute("srcset");
      img.removeAttribute("crossorigin");
      const src = originals[i]?.src;
      if (!src) {
        img.remove();
        return;
      }
      const data = src.startsWith("data:") ? src : await imgToDataURL(src);
      if (data) img.setAttribute("src", data);
      else img.remove(); // couldn't fetch it — drop rather than break/taint
    })
  );
}

/**
 * Chromium marks an SVG image as NOT having a "single security origin" when
 * its content references ANY external resource — @font-face urls, CSS
 * background images, @imports — even though SVG-as-image never actually
 * fetches them. Drawing such an image taints the canvas, and toBlob() throws
 * "Tainted canvases may not be exported" (the exact failure seen in the
 * field). Fragment refs (url(#gradient), used by chart fills) and data: URLs
 * are origin-clean and must survive.
 */
function stripExternalUrls(css: string): string {
  return css
    .replace(/@import[^;]*;/g, "")
    .replace(/url\((?!\s*['"]?\s*(?:#|data:))[^)]*\)/gi, "none");
}

/** Remove or inline everything in the clone that could taint the canvas. */
function scrubCloneForCanvas(node: HTMLElement, clone: HTMLElement): void {
  // Embedded browsing contexts and media can never render in SVG-as-image.
  clone.querySelectorAll("iframe, video, embed, object, audio").forEach((el) => el.remove());

  // Live canvases (e.g. a chart drawn via canvas): snapshot same-origin ones
  // into <img data:>, drop ones whose own taint makes toDataURL throw.
  const liveCanvases = Array.from(node.querySelectorAll("canvas"));
  Array.from(clone.querySelectorAll("canvas")).forEach((c, i) => {
    try {
      const live = liveCanvases[i];
      const data = live?.toDataURL("image/png");
      if (data) {
        const img = document.createElement("img");
        img.src = data;
        img.setAttribute("style", c.getAttribute("style") || "");
        if (live.width) img.width = live.width;
        if (live.height) img.height = live.height;
        c.replaceWith(img);
        return;
      }
    } catch {
      /* live canvas itself tainted — drop it */
    }
    c.remove();
  });

  // External url() references in inline styles taint just like stylesheet ones.
  clone.querySelectorAll("[style]").forEach((el) => {
    const style = el.getAttribute("style");
    if (style && /url\(/i.test(style)) el.setAttribute("style", stripExternalUrls(style));
  });
}

export async function exportPNG(title: string, node: HTMLElement): Promise<void> {
  const css = stripExternalUrls(await collectCSS());
  const clone = node.cloneNode(true) as HTMLElement;

  // Inline <img> sources first (pairs live/clone <img> lists by index, so it
  // must run before scrubCloneForCanvas, which can insert extra <img> nodes
  // into the clone when snapshotting live canvases).
  await inlineImages(node, clone);
  scrubCloneForCanvas(node, clone);

  const rect = node.getBoundingClientRect();
  const width = Math.ceil(rect.width);
  const height = Math.ceil(node.scrollHeight);
  const scale = 2;

  const serialized = new XMLSerializer().serializeToString(clone);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    `<foreignObject width="100%" height="100%">` +
    `<div xmlns="http://www.w3.org/1999/xhtml"><style>${cssForSvg(css)}</style>` +
    `<div style="width:${width}px;background:#fff">${serialized}</div></div>` +
    `</foreignObject></svg>`;

  // The SVG MUST be loaded through a data: URL, never a blob: URL. Verified
  // empirically (headless Chromium): drawing an SVG image that contains a
  // <foreignObject> taints the canvas whenever the image was loaded from a
  // blob: URL — even a minimal SVG with no CSS and no images — so toBlob()
  // throws "Tainted canvases may not be exported" no matter how thoroughly
  // the content is sanitised. The identical SVG loaded from a data: URL
  // keeps the canvas origin-clean. (The CSS/inline-style scrubbing above
  // stays as defence in depth for engines with content-based checks.)
  const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  await new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext("2d")!;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(scale, scale);
        ctx.drawImage(image, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) download(`${slug(title)}.png`, blob);
          resolve();
        }, "image/png");
      } catch (e) {
        reject(e);
      }
    };
    image.onerror = () => {
      reject(new Error("PNG rendering failed"));
    };
    image.src = svgUrl;
  });
}
